import { createEntityAdapter } from "@reduxjs/toolkit";

import apiSlice from "../api/apiSlice";
import { effectiveAtQuery, withBusinessValidity } from "../../utils/businessValidity";

const communitiesAdapter = createEntityAdapter({});
const initialState = communitiesAdapter.getInitialState({});

export const communitiesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* GET */
    getCommunitiesByUser: builder.query({
      query: () => ({
        url: `/communities`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      transformResponse: (responseData) => {
        if (responseData) {
          return communitiesAdapter.setAll(initialState, responseData);
        }
        return responseData;
      },
      providesTags: (result) => {
        if (result?.ids) {
          return [
            { type: "Community", id: "LIST" },
            ...result.ids.map((id) => ({ type: "Community", id })),
          ];
        }
        return [{ type: "Community", id: "LIST" }];
      },
    }),
    getCommunityById: builder.query({
      query: (communityId) => ({
        url: `/communities/${communityId}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: (_, __, communityId) => [{ type: "Community", id: communityId }],
    }),
    exportCommunity: builder.query({
      query: ({ communityId, mode = "snapshot", effectiveAt, startDate, endDate }) => {
        const params = new URLSearchParams({ mode });
        if (effectiveAt) params.set("effective_at", effectiveAt);
        if (startDate) params.set("start_date", startDate);
        if (endDate) params.set("end_date", endDate);
        return {
          url: `/communities/${communityId}/business-validity/export?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    getCommunityKpisById: builder.query({
      query: ({ communityId, startDate, endDate }) => ({
        url:
          `/communities/${communityId}/metrics/kpis?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Community-Kpis" }],
    }),
    getFlexibilityBids: builder.query({
      query: (communityId) => ({
        url: `/communities/${communityId}/flexibility/offers`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: (_, __, communityId) => [{ type: "FlexibilityBids", id: communityId }],
    }),
    getFlexibilityZone: builder.query({
      query({ communityId }) {
        return {
          url: `/communities/${communityId}/flexibility/flexibility_zone`,
          method: "GET",
          validateStatus: (response, result) => response.status === 200 && !result?.isError,
        };
      },
      providesTags: (_, __, { communityId }) => [{ type: "FlexibilityZone", id: communityId }],
    }),
    getCommunityACEnergyById: builder.query({
      query: ({ communityId, startDate, endDate, granularity }) => ({
        url:
          `/communities/${communityId}/metrics/ac_energy?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}&` +
          `granularity=${granularity}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Community-AC-Energy" }],
    }),
    getMembersKpisById: builder.query({
      query: ({ communityId, startDate, endDate }) => ({
        url:
          `/communities/${communityId}/members/metrics/kpis?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Members-Kpis" }],
    }),
    getCommunitySavingsById: builder.query({
      query: ({ communityId, startDate, endDate, granularity }) => ({
        url:
          `/communities/${communityId}/metrics/savings?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}&` +
          `granularity=${granularity}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Community-Savings" }],
    }),
    /* POST */
    addNewCommunity: builder.mutation({
      query: (community) => ({
        url: "/communities",
        method: "POST",
        body: {
          ...withBusinessValidity(community),
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const postResult = dispatch(
          communitiesApiSlice.util.updateQueryData("getCommunitiesByUser", undefined, (draft) => {
            draft.ids.push("NEW");
          })
        );
        try {
          await queryFulfilled;
        } catch {
          postResult.undo();
        }
      },
      invalidatesTags: [{ type: "Community", id: "LIST" }],
    }),
    runOperationTask: builder.mutation({
      query: ({ communityId, taskName }) => ({
        url: `/communities/${communityId}/operations/run?task_name=${taskName}`,
        method: "POST",
        cache: "no-cache",
      }),
    }),

    /* PUT */
    updateCommunity: builder.mutation({
      query: (community) => ({
        url: `/communities/${community.id}`,
        method: "PUT",
        body: {
          ...withBusinessValidity(community),
        },
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      invalidatesTags: (_, __, arg) => [{ type: "Community", id: arg.id }],
    }),
    updateDebugOptions: builder.mutation({
      query: ({ communityId, ...body }) => ({
        url: `/communities/${communityId}/debug`,
        method: "PUT",
        body,
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      invalidatesTags: (_, __, arg) => [{ type: "Community", id: arg.communityId }],
    }),
    deleteCommunityById: builder.mutation({
      query: ({ id }) => ({
        url: `/communities/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, arg) => [{ type: "Community", id: arg.id }],
    }),
    postFlexibilityConfig: builder.mutation({
      query: ({ communityId, flexibilityConfig }) => ({
        url: `/communities/${communityId}/flexibility/config`,
        method: "POST",
        body: flexibilityConfig,
        // eslint-disable-next-line no-unused-vars
        validateStatus: (response, result) => response.status === 201 || response.status === 200,
      }),
      invalidatesTags: (_, __, { communityId }) => [{ type: "Community", id: communityId }],
    }),
    deleteFlexibilityConfig: builder.mutation({
      query: ({ communityId }) => ({
        url: `/communities/${communityId}/flexibility/config`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { communityId }) => [{ type: "Community", id: communityId }],
    }),
    postEnergyManagementConfig: builder.mutation({
      query: ({ communityId, energyManagementConfig }) => ({
        url: `/communities/${communityId}/energy_management/config`,
        method: "POST",
        body: energyManagementConfig,
        // eslint-disable-next-line no-unused-vars
        validateStatus: (response, result) => response.status === 201 || response.status === 200,
      }),
      invalidatesTags: (_, __, { communityId }) => [{ type: "Community", id: communityId }],
    }),
    deleteEnergyManagementConfig: builder.mutation({
      query: ({ communityId }) => ({
        url: `/communities/${communityId}/energy_management/config`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { communityId }) => [{ type: "Community", id: communityId }],
    }),
    createFlexibilityZone: builder.mutation({
      query: ({ communityId, zoneName, zoneId }) => ({
        url: `/communities/${communityId}/flexibility/flexibility_zone`,
        method: "POST",
        body: { name: zoneName, id: zoneId },
        // eslint-disable-next-line no-unused-vars
        validateStatus: (response, result) => response.status === 201 || response.status === 200,
      }),
      invalidatesTags: (_, __, { communityId }) => [{ type: "FlexibilityZone", id: communityId }],
    }),
    deleteFlexibilityZone: builder.mutation({
      query: ({ communityId, zoneId }) => ({
        url: `/communities/${communityId}/flexibility/flexibility_zone/${zoneId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { communityId }) => [{ type: "FlexibilityZone", id: communityId }],
    }),

    /* UNIFIED EMS ENDPOINTS */
    getAvailableEMSModules: builder.query({
      query: (communityId) => ({
        url: `/communities/${communityId}/energy_management/modules`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: ["EMSModules"],
    }),
    getEMSModuleSchema: builder.query({
      query: ({ communityId, moduleType }) => ({
        url: `/communities/${communityId}/energy_management/modules/${moduleType}/schema`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: (_, __, { moduleType }) => [{ type: "EMSModuleSchema", id: moduleType }],
    }),
    getEMSConfigs: builder.query({
      query: (communityId) => ({
        url: `/communities/${communityId}/energy_management/configs`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: (result) => {
        if (result && Array.isArray(result)) {
          return [
            { type: "EMSConfig", id: "LIST" },
            ...result.map((config) => ({ type: "EMSConfig", id: config.id })),
          ];
        }
        return [{ type: "EMSConfig", id: "LIST" }];
      },
    }),
    createEMSConfig: builder.mutation({
      query: ({ communityId, configData }) => ({
        url: `/communities/${communityId}/energy_management/configs`,
        method: "POST",
        body: withBusinessValidity(configData),
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      invalidatesTags: (_, __, { communityId }) => [
        { type: "EMSConfig", id: "LIST" },
        { type: "Community", id: communityId },
      ],
    }),
    updateEMSConfig: builder.mutation({
      query: ({ communityId, configId, configData }) => ({
        url: `/communities/${communityId}/energy_management/configs/${configId}`,
        method: "PATCH",
        body: withBusinessValidity(configData),
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      invalidatesTags: (_, __, { communityId, configId }) => [
        { type: "EMSConfig", id: configId },
        { type: "EMSConfig", id: "LIST" },
        { type: "Community", id: communityId },
      ],
    }),
    deleteEMSConfig: builder.mutation({
      query: ({ communityId, configId, effectiveAt, endDate }) => ({
        url:
          `/communities/${communityId}/energy_management/configs/${configId}?` +
          `${effectiveAtQuery(effectiveAt)}` +
          `${endDate ? `&end_date=${encodeURIComponent(endDate)}` : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { communityId, configId }) => [
        { type: "EMSConfig", id: configId },
        { type: "EMSConfig", id: "LIST" },
        { type: "Community", id: communityId },
      ],
    }),
    getEMSOffers: builder.query({
      query: ({ communityId, moduleType, status, startDatetime, endDatetime }) => {
        let url = `/communities/${communityId}/energy_management/offers?module_type=${moduleType}`;
        if (status) url += `&status=${status}`;
        if (startDatetime) url += `&start_datetime=${startDatetime}`;
        if (endDatetime) url += `&end_datetime=${endDatetime}`;
        return {
          url,
          method: "GET",
          validateStatus: (response, result) => response.status === 200 && !result?.isError,
        };
      },
      providesTags: (result, _, { communityId, moduleType }) => {
        if (result && Array.isArray(result)) {
          return [
            { type: "EMSOffer", id: `${communityId}-${moduleType}` },
            ...result.map((offer) => ({ type: "EMSOffer", id: offer.id })),
          ];
        }
        return [{ type: "EMSOffer", id: `${communityId}-${moduleType}` }];
      },
    }),
    updateEMSOffer: builder.mutation({
      query: ({ communityId, offerId, updateData }) => ({
        url: `/communities/${communityId}/energy_management/offers/${offerId}`,
        method: "PATCH",
        body: updateData,
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      invalidatesTags: (_, __, { communityId, offerId }) => [
        { type: "EMSOffer", id: offerId },
        { type: "EMSOffer", id: `${communityId}-GDBN_BEFLEXIBLE` },
      ],
    }),
    triggerEMSCalculation: builder.mutation({
      query: ({ communityId, configId, targetDate }) => ({
        url: `/communities/${communityId}/energy_management/configs/${configId}/calculate${
          targetDate ? `?target_date=${targetDate}` : ""
        }`,
        method: "POST",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      invalidatesTags: (_, __, { communityId }) => [
        { type: "EMSConfig", id: "LIST" },
        { type: "EMSSetpoint", id: "LIST" },
        { type: "Community", id: communityId },
      ],
    }),
    getEMSMemberEnergy: builder.query({
      query: ({
        communityId,
        moduleType,
        cpeId,
        startDatetime,
        endDatetime,
        limit = 500,
        offset = 0,
      }) => {
        let url = `/communities/${communityId}/energy_management/member_energy?module_type=${moduleType}&limit=${limit}&offset=${offset}`;
        if (cpeId) url += `&cpe_id=${cpeId}`;
        if (startDatetime) url += `&start_datetime=${startDatetime}`;
        if (endDatetime) url += `&end_datetime=${endDatetime}`;
        return {
          url,
          method: "GET",
          validateStatus: (response, result) => response.status === 200 && !result?.isError,
        };
      },
      providesTags: (result, _, { communityId, moduleType }) => [
        { type: "EMSMemberEnergy", id: `${communityId}-${moduleType}` },
      ],
    }),
    getEMSBaseline: builder.query({
      query: ({
        communityId,
        moduleType,
        cpeId,
        startDatetime,
        endDatetime,
        limit = 500,
        offset = 0,
      }) => {
        let url = `/communities/${communityId}/energy_management/baseline?module_type=${moduleType}&limit=${limit}&offset=${offset}`;
        if (cpeId) url += `&cpe_id=${cpeId}`;
        if (startDatetime) url += `&start_datetime=${startDatetime}`;
        if (endDatetime) url += `&end_datetime=${endDatetime}`;
        return {
          url,
          method: "GET",
          validateStatus: (response, result) => response.status === 200 && !result?.isError,
        };
      },
      providesTags: (result, _, { communityId, moduleType }) => [
        { type: "EMSBaseline", id: `${communityId}-${moduleType}` },
      ],
    }),
    getOpportunityCostsByCommunity: builder.query({
      query: ({ communityId, startDatetime, endDatetime, isUpload, limit = 1000, offset = 0 }) => {
        let url = `/communities/${communityId}/opportunity_costs/by_community?limit=${limit}&offset=${offset}`;
        if (startDatetime) url += `&start_datetime=${startDatetime}`;
        if (endDatetime) url += `&end_datetime=${endDatetime}`;
        if (isUpload !== undefined && isUpload !== null) url += `&is_upload=${isUpload}`;
        return {
          url,
          method: "GET",
          validateStatus: (response, result) => response.status === 200 && !result?.isError,
        };
      },
      providesTags: (result, _, { communityId }) => [{ type: "OpportunityCost", id: communityId }],
    }),
    getLoadsForecastByCommunity: builder.query({
      query: ({ communityId, startDate, endDate, granularity = "MIN15" }) => ({
        url: `/communities/${communityId}/loads_forecast?start_date=${startDate}&end_date=${endDate}&granularity=${granularity}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: (result, _, { communityId }) => [{ type: "LoadsForecast", id: communityId }],
    }),
    getEMSSetpoints: builder.query({
      query: ({
        communityId,
        moduleType,
        startDatetime,
        endDatetime,
        resourceId,
        limit = 100,
        offset = 0,
      }) => {
        let url = `/communities/${communityId}/energy_management/setpoints?module_type=${moduleType}&limit=${limit}&offset=${offset}`;
        if (startDatetime) url += `&start_datetime=${startDatetime}`;
        if (endDatetime) url += `&end_datetime=${endDatetime}`;
        if (resourceId) url += `&resource_id=${resourceId}`;
        return {
          url,
          method: "GET",
          validateStatus: (response, result) => response.status === 200 && !result?.isError,
        };
      },
      providesTags: (result, _, { communityId, moduleType }) => {
        if (result?.data && Array.isArray(result.data)) {
          return [
            { type: "EMSSetpoint", id: `${communityId}-${moduleType}` },
            ...result.data.map((setpoint) => ({ type: "EMSSetpoint", id: setpoint.id })),
          ];
        }
        return [{ type: "EMSSetpoint", id: `${communityId}-${moduleType}` }];
      },
    }),
    exportEMSSetpoints: builder.mutation({
      query: ({ communityId, moduleType, startDatetime, endDatetime }) => {
        let url = `/communities/${communityId}/energy_management/setpoints/export?module_type=${moduleType}`;
        if (startDatetime) url += `&start_datetime=${startDatetime}`;
        if (endDatetime) url += `&end_datetime=${endDatetime}`;
        return {
          url,
          method: "GET",
          responseHandler: async (response) => response.blob(),
          cache: "no-cache",
        };
      },
    }),
    deleteEMSSetpoints: builder.mutation({
      query: ({ communityId, moduleType, startDatetime, endDatetime }) => {
        const params = new URLSearchParams({ module_type: moduleType });
        if (startDatetime) params.append("start_datetime", startDatetime);
        if (endDatetime) params.append("end_datetime", endDatetime);
        return {
          url: `/communities/${communityId}/energy_management/setpoints?${params.toString()}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (_, __, { communityId, moduleType }) => [
        { type: "EMSSetpoint", id: `${communityId}-${moduleType}` },
      ],
    }),
    getCommunityStructure: builder.query({
      query: (communityId) => ({
        url: `/communities/${communityId}/community_structure`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: (_, __, communityId) => [{ type: "CommunityStructure", id: communityId }],
    }),
    // Public (unauthenticated) platform totals for the login/landing screen.
    getLandingStats: builder.query({
      query: () => ({ url: `/public/landing-stats`, method: "GET" }),
    }),
  }),
});

export const {
  useGetLandingStatsQuery,
  useGetCommunitiesByUserQuery,
  useGetCommunityKpisByIdQuery,
  useAddNewCommunityMutation,
  useUpdateCommunityMutation,
  useUpdateDebugOptionsMutation,
  useDeleteCommunityByIdMutation,
  useGetMembersKpisByIdQuery,
  useGetCommunityACEnergyByIdQuery,
  useGetCommunitySavingsByIdQuery,
  useGetCommunityByIdQuery,
  useGetFlexibilityZoneQuery,
  useGetFlexibilityBidsQuery,
  usePostFlexibilityConfigMutation,
  useDeleteFlexibilityConfigMutation,
  usePostEnergyManagementConfigMutation,
  useDeleteEnergyManagementConfigMutation,
  useCreateFlexibilityZoneMutation,
  useDeleteFlexibilityZoneMutation,
  // Unified EMS hooks
  useGetAvailableEMSModulesQuery,
  useGetEMSModuleSchemaQuery,
  useGetEMSConfigsQuery,
  useCreateEMSConfigMutation,
  useUpdateEMSConfigMutation,
  useDeleteEMSConfigMutation,
  useGetEMSOffersQuery,
  useUpdateEMSOfferMutation,
  useTriggerEMSCalculationMutation,
  useGetEMSSetpointsQuery,
  useExportEMSSetpointsMutation,
  useDeleteEMSSetpointsMutation,
  useGetEMSMemberEnergyQuery,
  useGetEMSBaselineQuery,
  useGetOpportunityCostsByCommunityQuery,
  useGetLoadsForecastByCommunityQuery,
  useGetCommunityStructureQuery,
  useRunOperationTaskMutation,
} = communitiesApiSlice;

export const { selectAll: selectAllCommunities } = communitiesAdapter.getSelectors();
